import { api } from '@/lib/api';
import {
  type EventInfo,
  computePublicStats,
  createBlueprintReportCanvas,
  exportBlueprintToPDF,
} from '@/lib/mapExport';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { Seat, Shape } from '@eventflow/shared-types';
import type Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Image as KonvaImage, Layer, Line, Rect, Stage, Transformer } from 'react-konva';
import { useParams } from 'react-router-dom';
import useImage from 'use-image';
import {
  selectBackgroundImage,
  selectCanvasDimensions,
  selectSelectedShapeId,
  selectSelectedShapeIds,
  selectShapes,
  selectTool,
  selectZoom,
} from '../mapEditorSelectors';
import {
  addShape,
  clearSelection,
  copyShape,
  deleteShapes,
  pasteShape,
  pushHistory,
  redo,
  selectShape,
  setZoom,
  toggleSelectShape,
  undo,
  updateShape,
} from '../mapEditorSlice';

const hasHiddenFlag = (shape: Shape): shape is Shape & { hidden?: boolean } => {
  return 'hidden' in shape;
};

interface MapCanvasProps {
  seats?: Seat[];
  eventInfo?: EventInfo;
}

export default function MapCanvas({ seats = [], eventInfo }: MapCanvasProps) {
  const { id: eventId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const shapes = useAppSelector(selectShapes);
  const selectedShapeId = useAppSelector(selectSelectedShapeId);
  const selectedShapeIds = useAppSelector(selectSelectedShapeIds);
  const canvasDimensions = useAppSelector(selectCanvasDimensions);
  const tool = useAppSelector(selectTool);
  const zoom = useAppSelector(selectZoom);
  const backgroundImage = useAppSelector(selectBackgroundImage);

  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const mainMenuRef = useRef<HTMLDivElement | null>(null);
  const advancedBtnRef = useRef<HTMLButtonElement | null>(null);
  const advancedMenuRef = useRef<HTMLDivElement | null>(null);

  // Cargar imagen de fondo
  const [bgImage] = useImage(backgroundImage || '', 'anonymous');

  // Estado para pan (desplazamiento del canvas) - Sistema optimizado con inercia
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 0 });
  const stagePositionRef = useRef({ x: 0, y: 0 });
  const stagePositionFrame = useRef<number | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const lastTouchRef = useRef<{ x: number; y: number } | null>(null);

  // Sistema de inercia y suavizado
  const velocityRef = useRef({ x: 0, y: 0 });
  const lastMoveTimeRef = useRef(0);
  const inertiaFrameRef = useRef<number | null>(null);

  // Constantes de física del panning - SIMPLIFICADO
  const FRICTION = 0.95; // Factor de fricción para inercia
  const MIN_VELOCITY = 0.3; // Velocidad mínima antes de detener

  // Detener inercia
  const stopInertia = useCallback(() => {
    if (inertiaFrameRef.current !== null) {
      cancelAnimationFrame(inertiaFrameRef.current);
      inertiaFrameRef.current = null;
    }
    velocityRef.current = { x: 0, y: 0 };
  }, []);

  // Animación de inercia - SIMPLIFICADA sin límites
  const runInertia = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Aplicar fricción
    velocityRef.current.x *= FRICTION;
    velocityRef.current.y *= FRICTION;

    const speed = Math.sqrt(
      velocityRef.current.x * velocityRef.current.x + velocityRef.current.y * velocityRef.current.y
    );

    // Detener si la velocidad es muy baja
    if (speed < MIN_VELOCITY) {
      inertiaFrameRef.current = null;
      setStagePosition(stagePositionRef.current);
      return;
    }

    // Calcular nueva posición
    const newPos = {
      x: stagePositionRef.current.x + velocityRef.current.x,
      y: stagePositionRef.current.y + velocityRef.current.y,
    };

    // Actualizar posición
    stagePositionRef.current = newPos;
    stage.position(newPos);
    stage.batchDraw();

    // Continuar animación
    inertiaFrameRef.current = requestAnimationFrame(runInertia);
  }, []);
  const [contextMenu, setContextMenu] = useState<{ visible: boolean; x: number; y: number }>({
    visible: false,
    x: 0,
    y: 0,
  });
  const [contextAdvancedOpen, setContextAdvancedOpen] = useState<boolean>(false);
  const [submenuPos, setSubmenuPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  // Memo: estadísticas públicas basadas en shapes y seats reales (igual que vista pública)
  const publicStatsMemo = useMemo(() => {
    return computePublicStats({ shapes } as any, seats);
  }, [shapes, seats]);

  // Función para obtener el color de una shape basado en el estado real de los seats
  const getShapeColor = useCallback(
    (shape: Shape): string => {
      const tableId = (shape as any).tableId || (shape as any).seatIdentifier;
      if (!tableId) return '#22d3ee'; // Disponible por defecto

      // Buscar seats de esta mesa
      const tableSeats = seats.filter((seat) => {
        const seatTableId = seat.table_id || seat.seat_identifier.split('-').slice(0, -1).join('-');
        return seatTableId === tableId;
      });

      if (tableSeats.length === 0) return '#22d3ee'; // Disponible si no hay seats

      // Determinar el color basado en el estado de los seats
      const allBlocked = tableSeats.every((s) => s.status === 'blocked');
      const allSold = tableSeats.every((s) => s.status === 'sold');
      const allLocked = tableSeats.every((s) => s.status === 'locked');
      const someLocked = tableSeats.some((s) => s.status === 'locked');
      const someSold = tableSeats.some((s) => s.status === 'sold');

      if (allBlocked) return '#ef4444'; // Rojo - Bloqueado
      if (allSold) return '#6b7280'; // Gris - Vendido/Confirmado
      if (allLocked) return '#f59e0b'; // Naranja - Reservado/Locked
      if (someSold || someLocked) return '#f59e0b'; // Naranja - Parcialmente ocupado

      return '#22d3ee'; // Cyan - Disponible
    },
    [seats]
  );

  // Cerrar menú contextual y submenú al hacer clic/tocar fuera
  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (!contextMenu.visible && !contextAdvancedOpen) return;
      const target = e.target as Node;
      const insideMain = mainMenuRef.current ? mainMenuRef.current.contains(target) : false;
      const insideAdv = advancedMenuRef.current ? advancedMenuRef.current.contains(target) : false;
      if (!insideMain && !insideAdv) {
        setContextAdvancedOpen(false);
        setContextMenu({ visible: false, x: 0, y: 0 });
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [contextMenu.visible, contextAdvancedOpen]);

  // Estado para dibujo con drag
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingStart, setDrawingStart] = useState<{ x: number; y: number } | null>(null);
  const [tempShapeId, setTempShapeId] = useState<string | null>(null);

  // Sincronizar ref con state al montar o cuando stagePosition cambie externamente
  useEffect(() => {
    stagePositionRef.current = stagePosition;
  }, [stagePosition]);

  useEffect(() => {
    return () => {
      if (stagePositionFrame.current !== null) {
        cancelAnimationFrame(stagePositionFrame.current);
      }
    };
  }, []);

  // Actualizar transformer cuando cambia la selección
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const transformer = transformerRef.current;
      const stage = stageRef.current;

      const nodes = selectedShapeIds
        .map((id) => stage.findOne(`#${id}`))
        .filter(Boolean) as Konva.Node[];

      transformer.nodes(nodes);

      transformer.getLayer()?.batchDraw();
    }
  }, [selectedShapeIds]);

  // Manejar clic en el canvas - ahora solo deselecciona
  const handleStageClick = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const clickedOnEmpty = e.target === e.target.getStage();

      if (clickedOnEmpty && tool === 'select') {
        dispatch(clearSelection());
      }
    },
    [tool, dispatch]
  );

  // Menú contextual: abrir con click derecho
  const handleContextMenu = useCallback((e: any) => {
    e.evt.preventDefault();
    // Ubicar menú en posición absoluta del puntero
    const { clientX, clientY } = e.evt;
    setContextMenu({ visible: true, x: clientX, y: clientY });
    setContextAdvancedOpen(false);
  }, []);

  // Componer canvas del viewport con fondo blanco
  const buildViewportCanvasWithWhiteBg = (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return null;
    const pixelRatio = Math.max(1, Math.floor(scaleFactor));
    const canvas = stage.toCanvas({ pixelRatio });
    const expectedWidth = Math.round(stage.width() * pixelRatio);
    const expectedHeight = Math.round(stage.height() * pixelRatio);
    const merged = document.createElement('canvas');
    merged.width = expectedWidth;
    merged.height = expectedHeight;
    const mctx = merged.getContext('2d');
    if (!mctx) return null;
    // Fondo blanco para evitar transparencia
    mctx.fillStyle = '#ffffff';
    mctx.fillRect(0, 0, merged.width, merged.height);
    // Dibujar el contenido del Stage encima, ajustando a tamaño esperado
    mctx.drawImage(canvas, 0, 0, merged.width, merged.height);
    return merged;
  };

  // Exportación a PNG (descarga)
  const exportCurrentViewAsPng = async (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) {
      console.warn('[MapCanvas] Stage no disponible para exportación');
      return;
    }

    // Validar que haya imagen de fondo o al menos shapes
    const hasBg = !!bgImage;
    const hasShapes = shapes && shapes.length > 0;
    if (!hasBg && !hasShapes) {
      alert('No se puede descargar: no hay contenido para exportar.');
      return;
    }

    const merged = buildViewportCanvasWithWhiteBg(scaleFactor);
    if (!merged) {
      alert('No se puede descargar: error al generar la imagen.');
      return;
    }
    const dataURL = merged.toDataURL('image/png');

    // Validar dimensiones de la imagen generada
    const img = new Image();
    const expectedWidth = Math.round(
      stageRef.current!.width() * Math.max(1, Math.floor(scaleFactor))
    );
    const expectedHeight = Math.round(
      stageRef.current!.height() * Math.max(1, Math.floor(scaleFactor))
    );
    const ok = await new Promise<boolean>((resolve) => {
      img.onload = () => {
        const dimsOk = img.naturalWidth === expectedWidth && img.naturalHeight === expectedHeight;
        resolve(dimsOk);
      };
      img.onerror = () => resolve(false);
      img.src = dataURL;
    });
    if (!ok) {
      alert('No se puede descargar: la imagen generada no coincide con el tamaño esperado.');
      return;
    }

    const a = document.createElement('a');
    a.href = dataURL;
    const suffix = Math.max(1, Math.floor(scaleFactor));
    a.download = suffix > 1 ? `mapa-editor-${suffix}x.png` : 'mapa-editor.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setContextMenu({ visible: false, x: 0, y: 0 });

    // Logging de exportación
    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'image',
          scale: Math.max(1, Math.floor(scaleFactor)),
          source: 'map_editor',
          viewport: {
            x: stagePositionRef.current.x,
            y: stagePositionRef.current.y,
            width: stageRef.current!.width(),
            height: stageRef.current!.height(),
          },
        });
      }
    } catch (err) {
      console.warn('[MapCanvas] No se pudo registrar el log de exportación:', err);
    }
  };

  // Copiar imagen al portapapeles
  const copyCurrentViewToClipboard = async (scaleFactor = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return;
    const merged = buildViewportCanvasWithWhiteBg(scaleFactor);
    if (!merged) return;
    const blob: Blob | null = await new Promise((resolve) =>
      merged.toBlob((b) => resolve(b), 'image/png')
    );
    if (!blob) {
      alert('No se pudo copiar la imagen.');
      return;
    }
    try {
      // @ts-ignore ClipboardItem puede no estar tipado
      const item = new ClipboardItem({ [blob.type]: blob });
      await navigator.clipboard.write([item]);
      setContextMenu({ visible: false, x: 0, y: 0 });
      const suffix = Math.max(1, Math.floor(scaleFactor));
      alert(`Imagen copiada al portapapeles (${suffix}x).`);

      // Logging de copia
      try {
        if (eventId) {
          await api.post(`/events/${eventId}/log-export`, {
            exportType: 'copy',
            scale: Math.max(1, Math.floor(scaleFactor)),
            source: 'map_editor',
            viewport: {
              x: stagePositionRef.current.x,
              y: stagePositionRef.current.y,
              width: stageRef.current!.width(),
              height: stageRef.current!.height(),
            },
          });
        }
      } catch (err) {
        console.warn('[MapCanvas] No se pudo registrar el log de copia:', err);
      }
    } catch (err) {
      console.error('Clipboard write error:', err);
      alert('No se pudo copiar al portapapeles en este navegador.');
    }
  };

  // Acceso de organizador (control de frontend)
  const organizer = useAppSelector((s) => s.auth.organizer);

  // Exportar imagen con estadísticas (solo Organizadores) - Diseño blueprint profesional
  const exportStatsImage = async (quality = 1) => {
    const stage = stageRef.current as any;
    if (!stage) return;
    if (!organizer) {
      alert('Esta función requiere rol de Organizador.');
      return;
    }

    // quality: 1 = normal (pixelRatio 2), 3 = alta calidad (pixelRatio 3)
    const pixelRatio = quality === 3 ? 3 : 2;
    const stats = publicStatsMemo;

    // Usar información del evento si está disponible
    const eventInfoToUse: EventInfo = eventInfo || {
      title: 'Editor de Mapa',
      venue: undefined,
      date: undefined,
      time: undefined,
    };

    // Mostrar indicador de carga
    console.log(
      `📄 Generando reporte ${quality === 3 ? '(alta calidad)' : ''}... Por favor espera.`
    );

    // Usar setTimeout para permitir que el UI se actualice
    setTimeout(async () => {
      try {
        // Capturar el mapa visual
        const mapCanvas = buildViewportCanvasWithWhiteBg(pixelRatio);
        if (!mapCanvas) {
          alert('No se puede exportar: error al generar el mapa.');
          return;
        }

        // Crear canvas con diseño blueprint profesional
        const blueprintCanvas = createBlueprintReportCanvas(mapCanvas, stats, 1, eventInfoToUse);
        if (!blueprintCanvas) {
          alert('No se puede exportar: error al generar la imagen.');
          return;
        }

        const dataURL = blueprintCanvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        const qualityLabel = quality === 3 ? '-alta-calidad' : '';
        a.download = `reporte-mapa-editor${qualityLabel}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        console.log('✅ Reporte exportado exitosamente!');
      } catch (error) {
        console.error('❌ Error al exportar:', error);
        alert('Error al exportar el reporte.');
      }
      setContextMenu({ visible: false, x: 0, y: 0 });
      setContextAdvancedOpen(false);
    }, 100);

    // Logging de exportación con estadísticas
    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'stats',
          scale: pixelRatio,
          source: 'map_editor',
          viewport: {
            x: stagePositionRef.current.x,
            y: stagePositionRef.current.y,
            width: stageRef.current!.width(),
            height: stageRef.current!.height(),
          },
          stats,
        });
      }
    } catch (err) {
      console.warn('[MapCanvas] No se pudo registrar el log de estadísticas:', err);
    }
  };

  // Exportar estadísticas como PDF (solo Organizadores) - Máxima calidad
  const exportStatsPDF = async () => {
    const stage = stageRef.current as any;
    if (!stage) return;
    if (!organizer) {
      alert('Esta función requiere rol de Organizador.');
      return;
    }

    // Usar máxima calidad (pixelRatio 3) para PDF
    const pixelRatio = 3;
    const stats = publicStatsMemo;

    // Usar información del evento si está disponible
    const eventInfoToUse: EventInfo = eventInfo || {
      title: 'Editor de Mapa',
      venue: undefined,
      date: undefined,
      time: undefined,
    };

    console.log('📄 Generando reporte PDF (máxima calidad)... Por favor espera.');

    setTimeout(async () => {
      try {
        const mapCanvas = buildViewportCanvasWithWhiteBg(pixelRatio);
        if (!mapCanvas) {
          alert('No se puede exportar: error al generar el mapa.');
          return;
        }

        const blueprintCanvas = createBlueprintReportCanvas(mapCanvas, stats, 1, eventInfoToUse);
        if (!blueprintCanvas) {
          alert('No se puede exportar: error al generar la imagen.');
          return;
        }

        exportBlueprintToPDF(blueprintCanvas, 'reporte-mapa-editor.pdf');

        console.log('✅ Reporte PDF exportado exitosamente!');
      } catch (error) {
        console.error('❌ Error al exportar PDF:', error);
        alert('Error al exportar el reporte PDF.');
      }
      setContextMenu({ visible: false, x: 0, y: 0 });
      setContextAdvancedOpen(false);
    }, 100);

    try {
      if (eventId) {
        await api.post(`/events/${eventId}/log-export`, {
          exportType: 'stats',
          scale: pixelRatio,
          source: 'map_editor',
          viewport: {
            x: stagePositionRef.current.x,
            y: stagePositionRef.current.y,
            width: stageRef.current!.width(),
            height: stageRef.current!.height(),
          },
          stats: {
            totalSeats: stats.totalAsientos,
            available: stats.asientosDisponibles,
            sold: stats.asientosVendidosConfirmados,
            locked: stats.asientosReservadosPendientes,
            blocked: stats.asientosBloqueados,
          },
        });
      }
    } catch (err) {
      console.warn('[MapCanvas] No se pudo registrar el log de exportación PDF:', err);
    }
  };

  // Función para ajustar la vista al contenido (Fit to Screen)
  // Calcula el bounding box de las mesas VISIBLES (excluye ocultas y overlays)
  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    // Usar las dimensiones actuales del Stage (el área visible)
    const viewportWidth = stage.width();
    const viewportHeight = stage.height();

    // Filtrar solo mesas visibles (no ocultas, no overlays)
    const visibleTables = shapes.filter((shape) => {
      const isOverlay = (shape as any).category === 'overlay';
      const isHidden = (shape as any).hidden === true;
      return !isOverlay && !isHidden;
    });

    // Si no hay mesas visibles, usar dimensiones del canvas
    if (visibleTables.length === 0) {
      console.log('🔍 [MapCanvas] No hay mesas visibles, usando dimensiones del canvas');
      const newZoom = Math.min(
        viewportWidth / canvasDimensions.width,
        viewportHeight / canvasDimensions.height
      );
      dispatch(setZoom(newZoom));
      const newPos = { x: 0, y: 0 };
      stagePositionRef.current = newPos;
      setStagePosition(newPos);
      return;
    }

    // Calcular bounding box de las mesas visibles
    let minX = Number.POSITIVE_INFINITY,
      minY = Number.POSITIVE_INFINITY,
      maxX = Number.NEGATIVE_INFINITY,
      maxY = Number.NEGATIVE_INFINITY;

    for (const shape of visibleTables) {
      let shapeMinX = 0,
        shapeMinY = 0,
        shapeMaxX = 0,
        shapeMaxY = 0;

      if (shape.type === 'circle') {
        const radius = (shape as any).radius || 20;
        const cx = shape.x || 0;
        const cy = shape.y || 0;
        shapeMinX = cx - radius;
        shapeMinY = cy - radius;
        shapeMaxX = cx + radius;
        shapeMaxY = cy + radius;
      } else if (shape.type === 'rect') {
        const x = shape.x || 0;
        const y = shape.y || 0;
        const w = (shape as any).width || 40;
        const h = (shape as any).height || 40;
        shapeMinX = x;
        shapeMinY = y;
        shapeMaxX = x + w;
        shapeMaxY = y + h;
      } else if (shape.type === 'polygon' && (shape as any).points) {
        const points = (shape as any).points as number[];
        for (let i = 0; i < points.length; i += 2) {
          const px = points[i];
          const py = points[i + 1];
          shapeMinX = Math.min(shapeMinX || px, px);
          shapeMinY = Math.min(shapeMinY || py, py);
          shapeMaxX = Math.max(shapeMaxX || px, px);
          shapeMaxY = Math.max(shapeMaxY || py, py);
        }
      }

      minX = Math.min(minX, shapeMinX);
      minY = Math.min(minY, shapeMinY);
      maxX = Math.max(maxX, shapeMaxX);
      maxY = Math.max(maxY, shapeMaxY);
    }

    // Definir márgenes de seguridad para UI flotante
    // El panel de estadísticas está arriba a la izquierda (~220px ancho, ~100px alto)
    // Los controles de zoom están arriba a la derecha (pero son pequeños)
    const safeMargins = {
      top: 120, // Espacio para panel de estadísticas + margen
      left: 60, // Margen izquierdo generoso
      right: 60, // Margen derecho
      bottom: 60, // Margen inferior
    };

    // Calcular dimensiones disponibles reales para el contenido
    const availableWidth = viewportWidth - (safeMargins.left + safeMargins.right);
    const availableHeight = viewportHeight - (safeMargins.top + safeMargins.bottom);

    // Calcular dimensiones del contenido (mesas)
    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    // Calcular el zoom necesario para que el contenido quepa en el área segura
    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;

    // Usar el menor zoom y limitar a 95% del cálculo para dar un respiro extra
    const newZoom = Math.min(scaleX, scaleY) * 0.95;

    // Calcular el centro del bounding box de las mesas
    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;

    // Calcular el centro del área segura en coordenadas de pantalla
    // El centro del área segura está desplazado por los márgenes asimétricos
    const safeCenterX = safeMargins.left + availableWidth / 2;
    const safeCenterY = safeMargins.top + availableHeight / 2;

    // Calcular la posición del stage para alinear el centro del contenido con el centro seguro
    // Fórmula: pos = centro_pantalla - (centro_contenido * zoom)
    const newX = safeCenterX - contentCenterX * newZoom;
    const newY = safeCenterY - contentCenterY * newZoom;

    // Aplicar los cambios
    dispatch(setZoom(newZoom));

    // Actualizar posición del stage
    const newPos = { x: newX, y: newY };
    stagePositionRef.current = newPos;
    setStagePosition(newPos);
    stage.position(newPos);
    stage.batchDraw();

    console.log(
      `🔍 [MapCanvas] Fit to screen: ${visibleTables.length} mesas, zoom=${(newZoom * 100).toFixed(0)}%, margins={t:${safeMargins.top}, l:${safeMargins.left}}`
    );
  }, [shapes, canvasDimensions, dispatch]);

  // Escuchar eventos globales disparados desde la Toolbar
  useEffect(() => {
    const handleExportEvent = (e: Event) => {
      const ev = e as CustomEvent<{ scale?: number }>;
      const scale = ev.detail?.scale ?? 1;
      exportCurrentViewAsPng(scale);
    };
    const handleCopyEvent = (e: Event) => {
      const ev = e as CustomEvent<{ scale?: number }>;
      const scale = ev.detail?.scale ?? 1;
      copyCurrentViewToClipboard(scale);
    };
    const handleExportStatsEvent = (_e: Event) => {
      exportStatsImage();
    };
    const handleFitToScreenEvent = () => {
      fitToScreen();
    };
    window.addEventListener('mapEditor:exportImage', handleExportEvent as EventListener);
    window.addEventListener('mapEditor:copyImage', handleCopyEvent as EventListener);
    window.addEventListener('mapEditor:exportStatsImage', handleExportStatsEvent as EventListener);
    window.addEventListener('mapEditor:fitToScreen', handleFitToScreenEvent);
    return () => {
      window.removeEventListener('mapEditor:exportImage', handleExportEvent as EventListener);
      window.removeEventListener('mapEditor:copyImage', handleCopyEvent as EventListener);
      window.removeEventListener(
        'mapEditor:exportStatsImage',
        handleExportStatsEvent as EventListener
      );
      window.removeEventListener('mapEditor:fitToScreen', handleFitToScreenEvent);
    };
  }, [fitToScreen]);

  // Cerrar menús con tecla Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (contextMenu.visible || contextAdvancedOpen) {
          e.preventDefault();
          setContextAdvancedOpen(false);
          setContextMenu({ visible: false, x: 0, y: 0 });
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contextMenu.visible, contextAdvancedOpen]);

  // Crear una nueva shape según el tipo con tamaño inicial
  const createShape = (
    type: 'circle' | 'rect' | 'polygon' | 'overlay-rect' | 'overlay-circle',
    x: number,
    y: number,
    width?: number,
    height?: number
  ): Shape => {
    const id = `shape-${Date.now()}`;

    // --- Lógica para generar tableId con formato fijo: TABLE-{N} ---
    const existingTableIds = shapes.map((s) => (s as any).tableId || '');
    const tableNumbers = existingTableIds
      .map((id) => {
        // Formato estándar TABLE-{N}
        const match = id.match(/^TABLE-(\d+)$/i);
        return match ? Number.parseInt(match[1], 10) : 0;
      })
      .filter((num) => num > 0);

    const maxTableNumber = tableNumbers.length > 0 ? Math.max(...tableNumbers) : 0;
    // SIEMPRE usar formato fijo: TABLE-{N}
    const suggestedTableId = `TABLE-${maxTableNumber + 1}`;
    // ----------------------------------------

    const baseShape = {
      id,
      price: 100,
      fill: '#22d3ee',
      stroke: '#374151',
      hidden: false,
    };

    // Si es overlay, crear sin datos de mesa
    if (type === 'overlay-rect' || type === 'overlay-circle') {
      const overlayBase = {
        id,
        category: 'overlay',
        overlayFill: '#ffffff',
        overlayOpacity: 1,
        fill: '#ffffff',
        stroke: '#9CA3AF',
      };

      if (type === 'overlay-circle') {
        const radius = width ? Math.abs(width) / 2 : 20;
        return {
          ...overlayBase,
          type: 'circle',
          x,
          y,
          radius: Math.max(radius, 10),
        } as any as Shape;
      }

      // overlay-rect
      return {
        ...overlayBase,
        type: 'rect',
        x,
        y,
        width: width ? Math.abs(width) : 40,
        height: height ? Math.abs(height) : 40,
      } as any as Shape;
    }

    // Caso estándar: mesa (table)
    const tableBase = {
      ...baseShape,
      category: 'table',
      seatIdentifier: suggestedTableId, // Usar el ID de mesa como identificador inicial
      tableId: suggestedTableId, // Asignar el nuevo tableId
      tableLabel: `Mesa ${maxTableNumber + 1}`,
      capacity: 12,
    };

    switch (type) {
      case 'circle':
        const radius = width ? Math.abs(width) / 2 : 10;
        return {
          ...tableBase,
          type: 'circle',
          x,
          y,
          radius: Math.max(radius, 10), // Mínimo 10px para drag
        } as any as Shape;

      case 'rect':
        return {
          ...tableBase,
          type: 'rect',
          x,
          y,
          width: width ? Math.abs(width) : 20,
          height: height ? Math.abs(height) : 20,
        } as any as Shape;

      case 'polygon':
        // Triángulo basado en el tamaño arrastrado
        const size = width ? Math.abs(width) : 20;
        return {
          ...tableBase,
          type: 'polygon',
          points: [x + size / 2, y, x, y + size, x + size, y + size],
        } as any as Shape;

      default:
        return {
          ...baseShape,
          type: 'circle',
          x,
          y,
          radius: 20,
        } as any as Shape;
    }
  };

  // Renderizar shape según su tipo - OPTIMIZADO con useCallback
  const renderShape = useCallback(
    (shape: Shape) => {
      const handleSelect = (e: Konva.KonvaEventObject<MouseEvent>) => {
        if (tool === 'select') {
          const multi = e.evt.ctrlKey || e.evt.shiftKey || e.evt.metaKey;
          if (multi) {
            dispatch(toggleSelectShape(shape.id));
          } else {
            dispatch(selectShape(shape.id));
          }
        }
      };

      const isHidden = hasHiddenFlag(shape) && Boolean(shape.hidden);
      const isOverlay = (shape as any).category === 'overlay';
      // Usar color basado en estado real de seats (igual que vista pública)
      const fillColor = isOverlay
        ? (shape as any).overlayFill || shape.fill || '#ffffff'
        : getShapeColor(shape);
      const strokeColor = isOverlay ? '#9CA3AF' : shape.stroke || '#374151';

      if (isHidden) {
        console.log(
          '👻 [MapCanvas] Renderizando mesa oculta:',
          shape.tableId || shape.seatIdentifier
        );
      }

      const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;
        const updatedShape = { ...shape };

        if (shape.type === 'circle') {
          updatedShape.x = node.x();
          updatedShape.y = node.y();
        } else if (shape.type === 'rect') {
          updatedShape.x = node.x();
          updatedShape.y = node.y();
        } else if (shape.type === 'polygon') {
          updatedShape.x = node.x();
          updatedShape.y = node.y();
        }

        dispatch(updateShape(updatedShape));
        // Registrar estado final del movimiento para habilitar deshacer/rehacer
        dispatch(pushHistory());
      };

      const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
        const node = e.target;
        const updatedShape = { ...shape };

        if (shape.type === 'circle') {
          const scaleX = node.scaleX();
          updatedShape.radius = (shape.radius || 30) * scaleX;
          node.scaleX(1);
          node.scaleY(1);
        } else if (shape.type === 'rect') {
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          updatedShape.width = (shape.width || 60) * scaleX;
          updatedShape.height = (shape.height || 60) * scaleY;
          node.scaleX(1);
          node.scaleY(1);
        }

        updatedShape.rotation = node.rotation();
        dispatch(updateShape(updatedShape));
        // Registrar estado final de la transformación
        dispatch(pushHistory());
      };

      const commonProps = {
        id: shape.id,
        onClick: handleSelect,
        onTap: handleSelect,
        draggable: tool === 'select',
        onDragEnd: handleDragEnd,
        onTransformEnd: handleTransformEnd,
        fill: fillColor,
        stroke: isHidden ? '#9CA3AF' : strokeColor,
        strokeWidth: 2,
        opacity: isOverlay ? ((shape as any).overlayOpacity ?? 1) : isHidden ? 0.25 : 0.7,
        dash: isHidden ? [8, 4] : isOverlay ? [4, 4] : undefined,
        // OPTIMIZACIÓN: Prevenir re-renders innecesarios
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
      };

      switch (shape.type) {
        case 'circle':
          return (
            <Circle
              key={shape.id}
              {...commonProps}
              x={shape.x || 0}
              y={shape.y || 0}
              radius={shape.radius || 30}
              rotation={shape.rotation || 0}
            />
          );

        case 'rect':
          return (
            <Rect
              key={shape.id}
              {...commonProps}
              x={shape.x || 0}
              y={shape.y || 0}
              width={shape.width || 60}
              height={shape.height || 60}
              rotation={shape.rotation || 0}
            />
          );

        case 'polygon':
          return (
            <Line
              key={shape.id}
              {...commonProps}
              points={shape.points || [0, 0, 50, 0, 25, 50]}
              closed
              rotation={shape.rotation || 0}
            />
          );

        default:
          return null;
      }
    },
    [tool, dispatch, selectedShapeIds, getShapeColor]
  );

  // Manejar zoom con la rueda del mouse (muy lento) - ULTRA OPTIMIZADO
  const handleWheel = useCallback(
    (e: Konva.KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;

      const oldScale = stage.scaleX();
      const pointer = stage.getPointerPosition();
      if (!pointer) return;

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };

      // Zoom in/out (ultra lento: 0.01 para máximo control)
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const newScale = Math.max(0.1, Math.min(3, oldScale + direction * 0.01));

      const newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };

      // Actualizar directamente para mejor rendimiento
      stagePositionRef.current = newPos; // Actualizar ref
      stage.scale({ x: newScale, y: newScale });
      stage.position(newPos);
      stage.batchDraw();

      // Actualizar Redux y state (menos crítico)
      dispatch(setZoom(newScale));
      setStagePosition(newPos);
    },
    [dispatch]
  );

  // Zoom con botones (para futuro uso con UI de zoom)
  // const handleZoomIn = () => {
  //   const newZoom = Math.min(3, zoom + 0.1);
  //   dispatch(setZoom(newZoom));
  // };

  // const handleZoomOut = () => {
  //   const newZoom = Math.max(0.1, zoom - 0.1);
  //   dispatch(setZoom(newZoom));
  // };

  // const handleZoomReset = () => {
  //   dispatch(setZoom(1));
  //   setStagePosition({ x: 0, y: 0 });
  // };

  // Manejar inicio de dibujo o pan - OPTIMIZADO
  const handleMouseDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      // Detener cualquier inercia en curso al iniciar nueva interacción
      stopInertia();

      const clickedOnEmpty = e.target === e.target.getStage();
      const stage = e.target.getStage();
      if (!stage) return;

      const point = stage.getPointerPosition();
      if (!point) return;

      // Convertir coordenadas del stage a coordenadas del canvas (considerando zoom y posición)
      const canvasX = (point.x - stagePositionRef.current.x) / zoom;
      const canvasY = (point.y - stagePositionRef.current.y) / zoom;

      // Si estamos en modo dibujo (no select) y hacemos clic en vacío
      if (clickedOnEmpty && tool !== 'select') {
        setIsDrawing(true);
        setDrawingStart({ x: canvasX, y: canvasY });

        // Crear forma inicial mínima
        const newShape = createShape(tool as any, canvasX, canvasY);
        dispatch(addShape(newShape));
        setTempShapeId(newShape.id);
        return;
      }

      // Activar pan solo si estamos en modo select y hacemos clic en vacío
      if (clickedOnEmpty && tool === 'select') {
        // Inicializar tracking de velocidad
        lastMoveTimeRef.current = performance.now();
        velocityRef.current = { x: 0, y: 0 };
        setIsPanning(true);
        dispatch(clearSelection());
        return;
      }

      // Activar pan únicamente con middle click (botón central)
      if (e.evt.button === 1) {
        lastMoveTimeRef.current = performance.now();
        velocityRef.current = { x: 0, y: 0 };
        setIsPanning(true);
      }
    },
    [tool, zoom, dispatch, stopInertia]
  );

  const handleMouseMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>) => {
      const stage = stageRef.current;
      if (!stage) return;

      // Pan mode - Movimiento directo 1:1 con tracking de velocidad para inercia
      if (isPanning) {
        const now = performance.now();
        const dt = now - lastMoveTimeRef.current;
        lastMoveTimeRef.current = now;

        const dx = e.evt.movementX;
        const dy = e.evt.movementY;

        // Calcular velocidad instantánea para inercia
        if (dt > 0 && dt < 100) {
          const instantVx = (dx / dt) * 16;
          const instantVy = (dy / dt) * 16;
          velocityRef.current.x = velocityRef.current.x * 0.7 + instantVx * 0.3;
          velocityRef.current.y = velocityRef.current.y * 0.7 + instantVy * 0.3;
        }

        // Movimiento directo sin límites
        const newPos = {
          x: stagePositionRef.current.x + dx,
          y: stagePositionRef.current.y + dy,
        };

        stagePositionRef.current = newPos;
        stage.position(newPos);
        stage.batchDraw();

        // Sincronizar estado React
        if (stagePositionFrame.current === null) {
          stagePositionFrame.current = requestAnimationFrame(() => {
            stagePositionFrame.current = null;
            setStagePosition(stagePositionRef.current);
          });
        }

        return;
      }

      // Drawing mode - OPTIMIZADO: actualiza directamente el nodo sin Redux
      if (isDrawing && drawingStart && tempShapeId) {
        const point = stage.getPointerPosition();
        if (!point) return;

        // Convertir coordenadas usando el ref para evitar re-renders
        const canvasX = (point.x - stagePositionRef.current.x) / zoom;
        const canvasY = (point.y - stagePositionRef.current.y) / zoom;

        const width = canvasX - drawingStart.x;
        const height = canvasY - drawingStart.y;

        // Actualizar directamente el nodo de Konva sin Redux (más rápido)
        const node = stage.findOne(`#${tempShapeId}`);
        if (node) {
          if (tool === 'circle' || tool === 'overlay-circle') {
            const radius = Math.sqrt(width * width + height * height) / 2;
            node.setAttrs({ radius: Math.max(radius, 10) });
          } else if (tool === 'rect' || tool === 'overlay-rect') {
            node.setAttrs({
              width: Math.max(Math.abs(width), 10),
              height: Math.max(Math.abs(height), 10),
              x: width < 0 ? canvasX : drawingStart.x,
              y: height < 0 ? canvasY : drawingStart.y,
            });
          } else if (tool === 'polygon') {
            const size = Math.max(Math.abs(width), Math.abs(height), 20);
            node.setAttrs({
              points: [
                drawingStart.x + size / 2,
                drawingStart.y,
                drawingStart.x,
                drawingStart.y + size,
                drawingStart.x + size,
                drawingStart.y + size,
              ],
            });
          }
          node.getLayer()?.batchDraw();
        }
      }
    },
    [isPanning, isDrawing, drawingStart, tempShapeId, zoom, tool]
  );

  const handleMouseUp = useCallback(() => {
    if (isDrawing && tempShapeId && drawingStart) {
      const stage = stageRef.current;
      if (!stage) return;

      // Obtener valores actuales del nodo directamente
      const node = stage.findOne(`#${tempShapeId}`);
      if (node) {
        const tempShape = shapes.find((s) => s.id === tempShapeId);
        if (tempShape) {
          let wasSimpleClick = false;
          const updatedShape = { ...tempShape };

          // Verificar si el tamaño es muy pequeño (click sin arrastrar)
          if (tool === 'circle' || tool === 'overlay-circle') {
            const currentRadius = (node as any).radius();
            wasSimpleClick = currentRadius <= 15;
            if (wasSimpleClick) {
              updatedShape.radius = 20;
              node.setAttrs({ radius: 20 });
            } else {
              updatedShape.radius = currentRadius;
            }
          } else if (tool === 'rect' || tool === 'overlay-rect') {
            const currentWidth = (node as any).width();
            const currentHeight = (node as any).height();
            wasSimpleClick = currentWidth <= 25 && currentHeight <= 25;
            if (wasSimpleClick) {
              updatedShape.width = 40;
              updatedShape.height = 40;
              updatedShape.x = drawingStart.x - 20;
              updatedShape.y = drawingStart.y - 20;
            } else {
              updatedShape.width = currentWidth;
              updatedShape.height = currentHeight;
              updatedShape.x = node.x();
              updatedShape.y = node.y();
            }
          } else if (tool === 'polygon') {
            const currentPoints = (node as any).points();
            wasSimpleClick = Boolean(
              currentPoints &&
                currentPoints.length > 0 &&
                Math.abs(currentPoints[4] - currentPoints[0]) <= 30
            );
            if (wasSimpleClick) {
              const size = 40;
              const halfSize = size / 2;
              updatedShape.points = [
                drawingStart.x,
                drawingStart.y - halfSize,
                drawingStart.x - halfSize,
                drawingStart.y + halfSize,
                drawingStart.x + halfSize,
                drawingStart.y + halfSize,
              ];
            } else {
              updatedShape.points = currentPoints;
            }
          }

          // Sincronizar con Redux una sola vez al final
          dispatch(updateShape(updatedShape));
          node.getLayer()?.batchDraw();
        }
      }

      setIsDrawing(false);
      setDrawingStart(null);
      setTempShapeId(null);
      // Al finalizar el dibujo, registrar un snapshot en historial
      dispatch(pushHistory());
    }

    // Al terminar el panning, iniciar inercia si hay velocidad suficiente
    if (isPanning) {
      if (stagePositionFrame.current !== null) {
        cancelAnimationFrame(stagePositionFrame.current);
        stagePositionFrame.current = null;
      }

      const speed = Math.sqrt(
        velocityRef.current.x * velocityRef.current.x +
          velocityRef.current.y * velocityRef.current.y
      );

      // Solo iniciar inercia si hay velocidad significativa
      if (speed > MIN_VELOCITY * 2) {
        inertiaFrameRef.current = requestAnimationFrame(runInertia);
      } else {
        setStagePosition(stagePositionRef.current);
      }
    }

    setIsPanning(false);
    lastTouchRef.current = null;
  }, [isDrawing, tempShapeId, drawingStart, tool, shapes, dispatch, isPanning, runInertia]);

  // Touch handlers para panning en móvil - Sistema optimizado con inercia
  const handleTouchStart = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const stage = e.target.getStage();
      if (!stage) return;

      // Detener cualquier inercia en curso
      stopInertia();

      // Detectar si tocamos el fondo: el target es el Stage, Layer, o la imagen de fondo
      const targetName = e.target.name?.() || '';
      const targetClassName = e.target.getClassName?.() || '';
      const isStage = e.target === stage;
      const isLayer = targetClassName === 'Layer';
      const isBackgroundImage = targetClassName === 'Image' && !targetName; // Imagen sin nombre = fondo
      const clickedOnEmpty = isStage || isLayer || isBackgroundImage;

      // En modo select con un solo dedo, activar panning
      if (tool === 'select' && e.evt.touches.length === 1) {
        // Si tocamos una shape, no hacer pan (permitir selección)
        if (!clickedOnEmpty) return;

        const touch = e.evt.touches[0];
        lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
        lastMoveTimeRef.current = performance.now();
        velocityRef.current = { x: 0, y: 0 };
        setIsPanning(true);
        dispatch(clearSelection());

        // Prevenir scroll del navegador
        e.evt.preventDefault();
      }
    },
    [tool, dispatch, stopInertia]
  );

  const handleTouchMove = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      const stage = stageRef.current;
      if (!stage || !isPanning || !lastTouchRef.current) return;

      // Prevenir scroll del navegador mientras hacemos pan
      e.evt.preventDefault();

      if (e.evt.touches.length === 1) {
        const touch = e.evt.touches[0];
        const now = performance.now();
        const dt = now - lastMoveTimeRef.current;

        const dx = touch.clientX - lastTouchRef.current.x;
        const dy = touch.clientY - lastTouchRef.current.y;

        // Calcular velocidad instantánea para inercia
        if (dt > 0 && dt < 100) {
          const instantVx = (dx / dt) * 16;
          const instantVy = (dy / dt) * 16;
          velocityRef.current.x = velocityRef.current.x * 0.7 + instantVx * 0.3;
          velocityRef.current.y = velocityRef.current.y * 0.7 + instantVy * 0.3;
        }

        lastMoveTimeRef.current = now;
        lastTouchRef.current = { x: touch.clientX, y: touch.clientY };

        // Movimiento directo sin límites
        const newPos = {
          x: stagePositionRef.current.x + dx,
          y: stagePositionRef.current.y + dy,
        };

        stagePositionRef.current = newPos;
        stage.position(newPos);
        stage.batchDraw();

        if (stagePositionFrame.current === null) {
          stagePositionFrame.current = requestAnimationFrame(() => {
            stagePositionFrame.current = null;
            setStagePosition(stagePositionRef.current);
          });
        }
      }
    },
    [isPanning]
  );

  const handleTouchEnd = useCallback(() => {
    if (isPanning) {
      if (stagePositionFrame.current !== null) {
        cancelAnimationFrame(stagePositionFrame.current);
        stagePositionFrame.current = null;
      }

      const speed = Math.sqrt(
        velocityRef.current.x * velocityRef.current.x +
          velocityRef.current.y * velocityRef.current.y
      );

      // Iniciar inercia si hay velocidad suficiente
      if (speed > MIN_VELOCITY * 2) {
        inertiaFrameRef.current = requestAnimationFrame(runInertia);
      } else {
        setStagePosition(stagePositionRef.current);
      }
    }
    setIsPanning(false);
    lastTouchRef.current = null;
  }, [isPanning, runInertia]);

  // Manejar atajos de teclado (Copiar/Pegar/Eliminar/Deshacer/Rehacer)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar si el usuario está escribiendo en un input o textarea
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Ctrl+C o Cmd+C para copiar
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedShapeId && !isTyping) {
        e.preventDefault();
        dispatch(copyShape());
        console.log('📋 [MapEditor] Shape copiada');
      }

      // Ctrl+V o Cmd+V para pegar
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isTyping) {
        e.preventDefault();
        dispatch(pasteShape());
        console.log('📋 [MapEditor] Shape pegada');
      }

      // Delete o Backspace para eliminar (solo si NO estamos escribiendo)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping) {
        e.preventDefault();
        const ids =
          selectedShapeIds.length > 0 ? selectedShapeIds : selectedShapeId ? [selectedShapeId] : [];
        if (ids.length > 0) {
          dispatch(deleteShapes(ids));
          console.log(`🗑️ [MapEditor] Shapes eliminadas: ${ids.length}`);
        }
      }

      // Ctrl+Z para deshacer
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !isTyping) {
        e.preventDefault();
        dispatch(undo());
        console.log('↶ [MapEditor] Deshacer');
      }

      // Ctrl+Y para rehacer
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y' && !isTyping) {
        e.preventDefault();
        dispatch(redo());
        console.log('↷ [MapEditor] Rehacer');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedShapeId, selectedShapeIds, dispatch]);

  return (
    <div
      className="relative w-full h-full bg-gray-100 overflow-hidden"
      style={{ touchAction: 'none' }} // Prevenir gestos del navegador para panning suave
    >
      <Stage
        ref={stageRef}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
        scaleX={zoom}
        scaleY={zoom}
        x={stagePosition.x}
        y={stagePosition.y}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
        draggable={false}
        className="border border-gray-300"
        style={{
          cursor: isPanning ? 'grabbing' : tool === 'select' ? 'grab' : 'crosshair',
          touchAction: 'none', // También en el Stage
        }}
      >
        <Layer>
          {/* Imagen de fondo */}
          {bgImage && (
            <KonvaImage
              image={bgImage}
              width={canvasDimensions.width}
              height={canvasDimensions.height}
              listening={false}
              opacity={0.85}
            />
          )}

          {/* Asientos */}
          {shapes.map((shape) => renderShape(shape))}

          {/* Transformer para selección */}
          <Transformer ref={transformerRef} />
        </Layer>
      </Stage>
      {/* Menú contextual para exportar/copiar imagen */}
      {contextMenu.visible && (
        <div
          ref={mainMenuRef}
          className="fixed z-50 bg-neutral-900 text-white rounded-xl shadow-lg border border-neutral-700 overflow-hidden"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              exportCurrentViewAsPng(2);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
          >
            Guardar imagen...
          </button>
          {organizer && (
            <>
              <div className="border-t border-neutral-800" />
              <button
                onClick={() => {
                  exportStatsImage(1);
                }}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Guardar con estadísticas
              </button>
            </>
          )}
          <div className="border-t border-neutral-800" />
          <button
            onClick={() => {
              copyCurrentViewToClipboard(2);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
          >
            Copiar imagen
          </button>
          <div className="border-t border-neutral-800" />
          <button
            ref={advancedBtnRef}
            onClick={() => {
              const menuRect = mainMenuRef.current?.getBoundingClientRect();
              const advRect = advancedBtnRef.current?.getBoundingClientRect();
              if (menuRect && advRect) {
                setSubmenuPos({ left: Math.round(menuRect.right), top: Math.round(advRect.top) });
              } else {
                setSubmenuPos({ left: contextMenu.x + 220, top: contextMenu.y });
              }
              setContextAdvancedOpen((v) => !v);
            }}
            className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
          >
            Opciones avanzadas ▷
          </button>
        </div>
      )}
      {contextAdvancedOpen && (
        <div
          ref={advancedMenuRef}
          className="fixed z-50 bg-neutral-900 text-white rounded-xl shadow-lg border border-neutral-700 overflow-hidden"
          style={{ left: submenuPos.left, top: submenuPos.top }}
        >
          <button
            onClick={() => exportCurrentViewAsPng(3)}
            className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
          >
            Guardar imagen (maxima calidad)
          </button>
          <div className="border-t border-neutral-800" />
          <button
            onClick={() => copyCurrentViewToClipboard(3)}
            className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
          >
            Copiar imagen (maxima calidad)
          </button>
          {organizer && (
            <>
              <div className="border-t border-neutral-800" />
              <button
                onClick={() => exportStatsImage(3)}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Guardar estadísticas PNG (maxima calidad)
              </button>
              <div className="border-t border-neutral-800" />
              <button
                onClick={exportStatsPDF}
                className="block w-full text-left px-4 py-2 hover:bg-neutral-800"
              >
                Guardar estadísticas PDF (maxima calidad)
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
